import { models } from '@ps/web-core-store';
import type { Alias, Select as CoreSelect, Slicer as CoreSlicer } from '@ps/web-core-store';
import { canUseDOM, formatOptionalParams, stripBaseURL } from '@ps/web-core-utils';
import type { CasinoSearchParameters, Page, SEO, Website } from '@ps/web-martech-cms-types';
import { matchPath } from 'react-router';
import type { Selector } from 'reselect';
import URL from 'url-parse';

import { type RootState, type SelectorCreator, type Slicer, select } from '@swf/core/store';

import type { CMSState } from './index';

type MappedSelect = {
  [key in keyof CoreSelect['cms']]: () => (
    rootState: RootState
  ) => ReturnType<CoreSelect['cms'][key]>;
};

const selectors = (slice: Slicer<CMSState>, createSelector: SelectorCreator) => ({
  ...(models.cms.selectors(
    slice as CoreSlicer<CMSState>,
    createSelector
  ) as unknown as MappedSelect),
  enableOptimizely() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getToken(rootState, 'OptimizelyPaths'),
      (rootState: RootState) => select.location.getPathname(rootState),
      ({ page }, optimizelyPaths, pathname) => {
        if (page?.enable_optimizely || !optimizelyPaths) return !!page?.enable_optimizely;
        return new RegExp(optimizelyPaths).test(pathname);
      }
    );
  },
  getCacheControl() {
    return slice((state) => state.cacheControl);
  },
  getSEO() {
    return createSelector(
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (page, content) => {
        const refs = page?.content ? page.content[0].seo : undefined;
        return refs?.length ? (content[refs[0].uid] as SEO) : undefined;
      }
    );
  },
  getPage() {
    return slice((state) => state.page);
  },
  getSearchConfig() {
    return slice((state) => state.search);
  },
  getSearch() {
    return createSelector(
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (page, content) => {
        const ref = page?.casino_search;
        return ref?.length ? (content[ref[0].uid] as CasinoSearchParameters) : undefined;
      }
    );
  },
  getSuppressMarketingNotifications() {
    return createSelector(
      (rootState: RootState) => select.cms.getPage(rootState),
      (page) => !!page?.content?.[0].suppress_marketing_notifications
    );
  },
  getPageTitle: () =>
    createSelector(
      (rootState: RootState) => select.cms.getSEO(rootState)?.title,
      (rootState: RootState) => select.cms.getPage(rootState)?.title,
      (seoTitle, pageTitle) => seoTitle ?? pageTitle
    ),
  getPageLoading() {
    return slice((state) => canUseDOM && !state.pageLoaded);
  },
  getRedirect() {
    return slice((state) => state.redirect);
  },
  getRedirectTarget() {
    return slice(({ redirect, content }) => {
      const refs = redirect?.website;
      if (redirect?.to) return redirect.to;
      if (refs?.length) return (content[refs[0].uid] as Website).domains?.[0];
    });
  },
  getGeoRedirect() {
    return slice((state) => state.geo_redirect);
  },
  getGeoRedirectTarget() {
    return createSelector(
      slice,
      (rootState: RootState) => select.location.getProtocol(rootState),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ({ geo_redirect, content }, protocol) => {
        const refs = geo_redirect?.redirect_website;
        const page = geo_redirect?.to_page;
        if (refs?.length) {
          return new URL('')
            .set('protocol', protocol)
            .set('hostname', (content[refs[0].uid] as Website).domains?.[0])
            .set('pathname', page?.length ? (content[page[0]?.uid] as Page).url : '/').href;
        }
      }
    );
  },
  getPageMenu() {
    return slice(({ menu }) => menu);
  },
  getPageMenuDepth() {
    return slice(({ depth }) => depth);
  },
  getMenuView() {
    return slice(({ menuView }) => menuView);
  },
  getMenu() {
    return createSelector(
      (rootState: RootState) => select.cms.getPageMenu(rootState),
      (rootState: RootState) => select.cms.getPageMenuDepth(rootState),
      (rootState: RootState) => select.cms.getMenuView(rootState),
      (menu, depth, menuView) => ({ menu, depth, menuView })
    );
  },
  getSEOMenu() {
    return createSelector(slice, ({ seo_menu: seoMenu, seo_depth: seoDepth }) => ({
      seoMenu,
      seoDepth
    }));
  },
  getActiveAlias() {
    return createSelector(
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getAliases(rootState),
      (page, aliases) =>
        Object.keys(aliases).filter(
          (key) =>
            page?.url &&
            formatOptionalParams(aliases[key as Alias] ?? '') === formatOptionalParams(page?.url)
        )?.[0] as Alias
    );
  },
  matchAlias: () =>
    createSelector(
      (rootState: RootState) => select.cms.getUrlPathname(rootState),
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getAliases(rootState),
      (rootState: RootState) => select.cms.getActiveAlias(rootState),
      (pathname, page, aliases, activeAlias) => {
        const alias = page?.alias || activeAlias;
        return alias
          ? matchPath(pathname, { path: aliases[alias], exact: true, strict: false })
          : null;
      }
    ),
  getUrl: () => slice((state) => state.url),
  getUrlPathname: () =>
    createSelector(
      (rootState: RootState) => select.cms.getUrl(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (url, locale) => stripBaseURL(url?.pathname || '/', locale)
    ),
  getUrlParams: () =>
    createSelector(
      (rootState: RootState) => select.cms.matchAlias(rootState),
      (match) => (match ? match.params : {}) as Record<string, string>
    ),
  getPageAlternateLinks() {
    return createSelector(
      slice,
      (rootState: RootState) =>
        (select.location.stripPrefix as Selector<RootState, string>)(rootState),
      ({ alternate_links: alternateLinks }, strippedPathname) =>
        alternateLinks?.map(
          ({
            link: {
              locale_code: localeCode,
              base_url: baseUrl,
              replaced_path_start: replacedPathStart
            }
          }) => {
            const linkPath =
              replacedPathStart?.path_start_to_replace && replacedPathStart?.new_path_start
                ? strippedPathname.replace(
                    new RegExp(`^${replacedPathStart.path_start_to_replace}`),
                    replacedPathStart.new_path_start
                  )
                : strippedPathname;

            return {
              localeCode,
              url: `${baseUrl}${linkPath}`
            };
          }
        )
    );
  }
});

export default selectors;
